import type { ApiResponse, PurchaseRequest, Ticket, TicketValidationResult } from "@/types";
import type { ITicketService } from "../types";
import { httpClient } from "@services/http/httpClient";
import { legacyHttpClient } from "@services/http/legacyHttpClient";
import { PAYMENT_METHOD_CODES } from "@/types";
import type { SaveSaleRequest, SaleResponse } from "@/types";

interface MobileTicket {
  id: string;
  orderId: string;
  eventId: string;
  localityName: string;
  code: string;
  qr: string;
  state: number;
}

/**
 * Production ticket service using the Java mobile API.
 * - getMyTickets: GET tickets?userId={uid} — returns QRs base64 directly
 * - purchase: still uses legacy save_sale (order creation unchanged)
 */
export const mobileTicketService: ITicketService = {
  async purchase(
    data: PurchaseRequest,
  ): Promise<ApiResponse<{ orderId: string; total: number }>> {
    try {
      const upperNombre = (data.nombre ?? "").toUpperCase();
      const localPhone = (data.phone ?? "").replace(/^\+\d{1,3}/, "0");
      const saleRequest: SaveSaleRequest = {
        id: Number(data.localityId),
        cantidad: data.cantidad,
        email: data.email,
        pago: PAYMENT_METHOD_CODES[data.formapago] ?? 1,
        origen: "WEB",
        whatsapp: data.phone,
        seats: data.seats,
        cedula: data.cedula,
        nombre: upperNombre,
        user: {
          name: upperNombre,
          email: data.email,
          phone: localPhone,
          address: "",
          uuid: data.cedula ?? "",
        },
      };

      const orderIdRaw = await legacyHttpClient.post<string | number>(
        "/api/web/save_sale",
        saleRequest,
      );
      const orderId = String(orderIdRaw);

      const sale = await legacyHttpClient.post<SaleResponse>(
        "/api/web/get_sale_by_id",
        { id: orderId },
      );

      return {
        success: true,
        data: {
          orderId,
          // Plux cobra sale.total = precio + COMISIÓN (ej. $8.80), aprobado por negocio 2026-09-03.
          // OJO: sale.amount es el SUBTOTAL (SIN comisión, ej. $8.00) — NO usarlo para cobrar.
          // Consistente con PayPhone (totalpb) y la App (useCheckout usa sale.total).
          total: Number.parseFloat(String(sale.total)),
        },
      };
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "object" && error !== null && "message" in error
            ? String((error as { message: unknown }).message)
            : "Error al procesar la compra";
      return { success: false, data: { orderId: "", total: 0 }, message };
    }
  },

  /**
   * Fetches tickets for a user from the mobile API.
   * @param userId - Firebase UID of the authenticated user
   */
  async getMyTickets(userId: string): Promise<ApiResponse<Ticket[]>> {
    try {
      const result = await httpClient.get<MobileTicket[]>("tickets", {
        params: { userId },
      });

      const items = result.data ?? [];

      if (!Array.isArray(items)) {
        return { success: true, data: [] };
      }

      const tickets: Ticket[] = items.map((item) => ({
        id: item.id,
        orderId: item.orderId,
        eventId: item.eventId,
        localityName: item.localityName ?? "General",
        code: item.code,
        qr: item.qr,
        state: item.state,
      }));

      return { success: true, data: tickets };
    } catch (error) {
      return {
        success: false,
        data: [],
        message:
          error instanceof Error
            ? error.message
            : "Error al cargar tus tickets",
      };
    }
  },

  async validate(): Promise<ApiResponse<TicketValidationResult>> {
    return {
      success: false,
      data: { valid: false, message: "Validación no disponible en la web" },
    };
  },
};
